import {
  ArrowRightStartOnRectangleIcon,
  Bars3Icon,
  BookOpenIcon,
  ChevronUpIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  EnvelopeIcon,
  HomeIcon,
  RocketLaunchIcon,
  ShareIcon,
  ShieldCheckIcon,
  UserCircleIcon,
  UsersIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import professionalLogo from '@img/logo/logo_professional.svg';
import professionalLogoDark from '@img/logo/logo_professional_dark.svg';
import {Button, Dialog, DialogBody, DialogHeader} from '@design-system';
import {
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type MouseEvent as ReactMouseEvent,
  type SVGProps,
} from 'react';
import {Link, Outlet, useLocation, useNavigate} from 'react-router';

import {useAuth} from '../Auth/useAuth';
import FeedbackPopup from '../Shared/FeedbackPopup';
import {DIALOG_URL_PARAMS} from '../Util/constants';
import useDialogQuery from '../Util/useDialogQuery';

type NavigationIcon = ComponentType<SVGProps<SVGSVGElement>>;
type RouteMatch = 'exact' | 'section';
type NavigationAction = 'glossary' | 'logout';
type MobileNavigationHandoff =
  | {kind: 'destination'; href: string}
  | {kind: 'action'; action: NavigationAction};

type NavigationDestination = {
  kind: 'destination';
  href: string;
  label: string;
  Icon: NavigationIcon;
  match?: RouteMatch;
  rel?: string;
  target?: '_blank';
};

type NavigationButton = {
  kind: 'action';
  action: NavigationAction;
  label: string;
  Icon: NavigationIcon;
};

type NavigationEntry = NavigationDestination | NavigationButton;

// The two primary matrices remain explicit even though they intentionally have
// the same entries. That keeps a future mobile-only product change reviewable
// instead of silently changing desktop navigation through a shared array.
const DESKTOP_PRIMARY_ENTRIES = [
  {kind: 'destination', href: '/dashboard', label: 'Home', Icon: HomeIcon, match: 'exact'},
  {
    kind: 'destination',
    href: '/timeline',
    label: 'Transition Timeline',
    Icon: ClockIcon,
    match: 'exact',
  },
  {
    kind: 'destination',
    href: '/parent-categories',
    label: 'Parent Resources',
    Icon: UsersIcon,
    match: 'section',
  },
  {
    kind: 'destination',
    href: '/young-person-categories',
    label: 'Young Person Resources',
    Icon: RocketLaunchIcon,
    match: 'section',
  },
  {
    kind: 'destination',
    href: '/key-duties',
    label: 'Professional Key Duties',
    Icon: ClipboardDocumentCheckIcon,
    match: 'section',
  },
  {
    kind: 'destination',
    href: '/manage-shares',
    label: 'Manage Shares',
    Icon: ShareIcon,
    match: 'exact',
  },
  {kind: 'action', action: 'glossary', label: 'Transitions A-Z', Icon: BookOpenIcon},
] as const satisfies readonly NavigationEntry[];

const MOBILE_PRIMARY_ENTRIES = [
  {kind: 'destination', href: '/dashboard', label: 'Home', Icon: HomeIcon, match: 'exact'},
  {
    kind: 'destination',
    href: '/timeline',
    label: 'Transition Timeline',
    Icon: ClockIcon,
    match: 'exact',
  },
  {
    kind: 'destination',
    href: '/parent-categories',
    label: 'Parent Resources',
    Icon: UsersIcon,
    match: 'section',
  },
  {
    kind: 'destination',
    href: '/young-person-categories',
    label: 'Young Person Resources',
    Icon: RocketLaunchIcon,
    match: 'section',
  },
  {
    kind: 'destination',
    href: '/key-duties',
    label: 'Professional Key Duties',
    Icon: ClipboardDocumentCheckIcon,
    match: 'section',
  },
  {
    kind: 'destination',
    href: '/manage-shares',
    label: 'Manage Shares',
    Icon: ShareIcon,
    match: 'exact',
  },
  {kind: 'action', action: 'glossary', label: 'Transitions A-Z', Icon: BookOpenIcon},
] as const satisfies readonly NavigationEntry[];

const DESKTOP_USER_ENTRIES = [
  {
    kind: 'destination',
    href: '/my-account',
    label: 'My Account',
    Icon: UserCircleIcon,
    match: 'exact',
  },
  {kind: 'destination', href: '/manage-shares', label: 'Manage Shares', Icon: ShareIcon},
  {
    kind: 'destination',
    href: '/privacy',
    label: 'Privacy Notice',
    Icon: ShieldCheckIcon,
    target: '_blank',
    rel: 'noreferrer',
  },
  {kind: 'destination', href: '/contact-us', label: 'Contact Us', Icon: EnvelopeIcon},
  {kind: 'action', action: 'logout', label: 'Sign Out', Icon: ArrowRightStartOnRectangleIcon},
] as const satisfies readonly NavigationEntry[];

const MOBILE_SUPPLEMENTAL_ENTRIES = [
  {
    kind: 'destination',
    href: '/my-account',
    label: 'My Account',
    Icon: UserCircleIcon,
    match: 'exact',
  },
  // Intentionally duplicated from the mobile primary group. This is part of
  // the established product matrix; only the primary copy owns current state.
  {kind: 'destination', href: '/manage-shares', label: 'Manage Shares', Icon: ShareIcon},
  {kind: 'destination', href: '/privacy', label: 'Privacy Notice', Icon: ShieldCheckIcon},
  {kind: 'destination', href: '/contact-us', label: 'Contact', Icon: EnvelopeIcon},
  {kind: 'action', action: 'logout', label: 'Sign Out', Icon: ArrowRightStartOnRectangleIcon},
] as const satisfies readonly NavigationEntry[];

const NAVIGATION_CONTROL_CLASSES = [
  'relative flex min-h-11 w-full cursor-pointer items-center gap-3 rounded-md border-l-4 border-transparent px-3 py-2 text-left text-sm font-medium no-underline',
  'text-(--compass-on-surface) hover:bg-(--compass-surface-muted)',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--compass-focus-ring)',
].join(' ');

const CURRENT_NAVIGATION_CLASSES =
  'border-(--compass-primary) bg-(--compass-surface-muted) font-bold';

const isCurrentDestination = (
  pathname: string,
  destination: Pick<NavigationDestination, 'href' | 'match'>
) => {
  if (!destination.match) {
    return false;
  }

  if (destination.match === 'exact') {
    return pathname === destination.href;
  }

  return pathname === destination.href || pathname.startsWith(`${destination.href}/`);
};

const ProfessionalLogos = ({
  onNavigate,
}: {
  onNavigate?: (event: ReactMouseEvent<HTMLAnchorElement>) => void;
}) => (
  <Link
    to="/dashboard"
    onClick={onNavigate}
    className="inline-flex min-h-11 min-w-11 items-center rounded-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--compass-focus-ring)">
    <img className="block h-10 w-auto dark:hidden" src={professionalLogo} alt="Compass Homepage" />
    <img
      className="hidden h-10 w-auto dark:block"
      src={professionalLogoDark}
      alt="Compass Homepage"
    />
  </Link>
);

type NavigationListProps = {
  entries: readonly NavigationEntry[];
  pathname: string;
  onAction: (action: NavigationAction) => void;
  onDestination?: (
    destination: NavigationDestination,
    event: ReactMouseEvent<HTMLAnchorElement>
  ) => void;
  testId: string;
};

const NavigationList = ({
  entries,
  pathname,
  onAction,
  onDestination,
  testId,
}: NavigationListProps) => (
  <ul className="m-0 flex list-none flex-col gap-1 p-0" data-testid={testId}>
    {entries.map((entry) => {
      const Icon = entry.Icon;

      if (entry.kind === 'action') {
        return (
          <li key={entry.label}>
            <button
              type="button"
              className={NAVIGATION_CONTROL_CLASSES}
              onClick={() => onAction(entry.action)}>
              <Icon className="size-6 shrink-0" aria-hidden="true" />
              <span>{entry.label}</span>
            </button>
          </li>
        );
      }

      const current = isCurrentDestination(pathname, entry);

      return (
        <li key={`${entry.href}-${entry.label}`}>
          <Link
            to={entry.href}
            target={entry.target}
            rel={entry.rel}
            aria-current={current ? 'page' : undefined}
            className={`${NAVIGATION_CONTROL_CLASSES} ${current ? CURRENT_NAVIGATION_CLASSES : ''}`}
            onClick={(event) => onDestination?.(entry, event)}>
            <Icon className="size-6 shrink-0" aria-hidden="true" />
            <span>{entry.label}</span>
            {current ? (
              <span className="ml-auto text-xs font-bold" aria-hidden="true">
                Current
              </span>
            ) : null}
          </Link>
        </li>
      );
    })}
  </ul>
);

type DesktopUserNavigationProps = {
  email?: string;
  name?: string;
  pathname: string;
  onLogout: () => void;
};

const DesktopUserNavigation = ({email, name, pathname, onLogout}: DesktopUserNavigationProps) => {
  const [open, setOpen] = useState(false);
  const disclosureRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const closeFromOutside = (event: PointerEvent) => {
      if (!disclosureRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const closeFromEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', closeFromOutside);
    document.addEventListener('keydown', closeFromEscape);

    return () => {
      document.removeEventListener('pointerdown', closeFromOutside);
      document.removeEventListener('keydown', closeFromEscape);
    };
  }, [open]);

  const close = () => setOpen(false);

  return (
    <div className="relative" ref={disclosureRef}>
      <Button
        ref={triggerRef}
        id="user-menu"
        variant="ghost"
        className="h-auto min-h-11 w-full justify-start px-3 py-2 text-left"
        aria-expanded={open}
        aria-controls="professional-user-navigation"
        onClick={() => setOpen((isOpen) => !isOpen)}>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold">{name}</span>
          <span className="block truncate text-xs font-normal text-(--compass-on-surface)">
            {email}
          </span>
        </span>
        <ChevronUpIcon
          className={`size-5 shrink-0 ${open ? '' : 'rotate-180'} motion-reduce:transition-none`}
          aria-hidden="true"
        />
      </Button>
      <nav
        id="professional-user-navigation"
        aria-label="Professional account navigation"
        hidden={!open}
        className="absolute bottom-[calc(100%+0.5rem)] left-0 z-20 w-full rounded-md border border-(--compass-border) bg-(--compass-surface) p-2 shadow-lg">
        <NavigationList
          entries={DESKTOP_USER_ENTRIES}
          pathname={pathname}
          testId="desktop-user-navigation"
          onDestination={close}
          onAction={(action) => {
            close();
            if (action === 'logout') {
              onLogout();
            }
          }}
        />
      </nav>
    </div>
  );
};

const MOBILE_NAVIGATION_TITLE_ID = 'professional-navigation-title';

const Layout = () => {
  const {authState, showLogoutDialog} = useAuth();
  const {openDialog} = useDialogQuery();
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
  const closeNavigationRef = useRef<HTMLButtonElement | null>(null);
  const pendingMobileHandoffRef = useRef<MobileNavigationHandoff | null>(null);

  const closeMobileNavigation = () => setMobileNavigationOpen(false);
  const handleAction = (action: NavigationAction) => {
    if (action === 'glossary') {
      openDialog(DIALOG_URL_PARAMS.GLOSSARY);
    } else {
      showLogoutDialog();
    }
  };
  const queueMobileAction = (action: NavigationAction) => {
    pendingMobileHandoffRef.current = {kind: 'action', action};
    closeMobileNavigation();
  };
  const queueMobileDestination = (href: string, event: ReactMouseEvent<HTMLAnchorElement>) => {
    const isSameContextActivation =
      event.button === 0 &&
      !event.altKey &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.shiftKey &&
      !event.defaultPrevented;

    if (isSameContextActivation) {
      event.preventDefault();
      pendingMobileHandoffRef.current = {kind: 'destination', href};
    }

    closeMobileNavigation();
  };

  // The native dialog is removed before a destination, glossary, or logout
  // handoff runs. That releases browser inertness and restores the opener first,
  // so the receiving route or overlay never competes with a closing focus trap.
  useEffect(() => {
    if (mobileNavigationOpen) {
      return;
    }

    const handoff = pendingMobileHandoffRef.current;

    if (!handoff) {
      return;
    }

    pendingMobileHandoffRef.current = null;

    if (handoff.kind === 'destination') {
      navigate(handoff.href);
    } else if (handoff.action === 'glossary') {
      openDialog(DIALOG_URL_PARAMS.GLOSSARY);
    } else {
      showLogoutDialog();
    }
  }, [mobileNavigationOpen, navigate, openDialog, showLogoutDialog]);

  useEffect(() => {
    const desktopQuery = window.matchMedia('(min-width: 992px)');
    const closeAtDesktop = (event: MediaQueryListEvent) => {
      if (event.matches) {
        setMobileNavigationOpen(false);
      }
    };

    desktopQuery.addEventListener('change', closeAtDesktop);

    return () => desktopQuery.removeEventListener('change', closeAtDesktop);
  }, []);

  return (
    <>
      <div
        className="relative isolate flex min-h-svh min-w-0 flex-1 flex-col bg-(--compass-surface-muted) text-(--compass-on-surface) lg:flex-row"
        data-testid="professional-shell">
        <div className="contents" data-testid="professional-shell-chrome">
          <aside className="fixed inset-y-0 left-0 z-10 hidden w-64 border-r border-(--compass-border) bg-(--compass-surface) lg:flex">
            <nav
              className="flex h-full min-h-0 w-full flex-col"
              aria-label="Professional primary navigation">
              <div className="shrink-0 border-b border-(--compass-border) p-4">
                <ProfessionalLogos />
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto p-4">
                <NavigationList
                  entries={DESKTOP_PRIMARY_ENTRIES}
                  pathname={pathname}
                  testId="desktop-primary-navigation"
                  onAction={handleAction}
                />
              </div>
              <div className="shrink-0 border-t border-(--compass-border) p-4">
                <DesktopUserNavigation
                  name={authState.user?.name}
                  email={authState.user?.email}
                  pathname={pathname}
                  onLogout={showLogoutDialog}
                />
              </div>
            </nav>
          </aside>

          <header className="flex min-h-16 shrink-0 items-center border-b border-(--compass-border) bg-(--compass-surface) px-4 lg:hidden">
            <Button
              variant="ghost"
              size="icon"
              className="size-11"
              aria-label="Open navigation"
              onClick={() => setMobileNavigationOpen(true)}>
              <Bars3Icon className="size-6" aria-hidden="true" />
            </Button>
          </header>

          {mobileNavigationOpen ? (
            <Dialog
              open={mobileNavigationOpen}
              onClose={closeMobileNavigation}
              initialFocusRef={closeNavigationRef}
              aria-labelledby={MOBILE_NAVIGATION_TITLE_ID}
              data-testid="mobile-navigation-dialog"
              className="fixed inset-y-0 left-0 m-0 h-dvh max-h-dvh w-80 max-w-full rounded-none border-y-0 border-l-0 lg:hidden max-sm:m-0 max-sm:h-dvh max-sm:max-h-dvh max-sm:w-80 max-sm:max-w-full max-sm:rounded-none max-sm:border-r">
              <DialogHeader className="justify-between">
                <ProfessionalLogos onNavigate={(event) => queueMobileDestination('/dashboard', event)} />
                <h2 id={MOBILE_NAVIGATION_TITLE_ID} className="sr-only">
                  Professional navigation
                </h2>
                <Button
                  ref={closeNavigationRef}
                  variant="ghost"
                  size="icon"
                  className="size-11 shrink-0"
                  aria-label="Close navigation"
                  onClick={closeMobileNavigation}>
                  <XMarkIcon className="size-6" aria-hidden="true" />
                </Button>
              </DialogHeader>
              <DialogBody className="flex flex-col gap-4">
                <nav aria-label="Professional primary navigation">
                  <NavigationList
                    entries={MOBILE_PRIMARY_ENTRIES}
                    pathname={pathname}
                    testId="mobile-primary-navigation"
                    onDestination={(destination, event) =>
                      queueMobileDestination(destination.href, event)
                    }
                    onAction={queueMobileAction}
                  />
                </nav>
                <nav
                  aria-label="Professional supplemental navigation"
                  className="border-t border-(--compass-border) pt-4">
                  <NavigationList
                    entries={MOBILE_SUPPLEMENTAL_ENTRIES}
                    pathname={pathname}
                    testId="mobile-supplemental-navigation"
                    onDestination={(destination, event) =>
                      queueMobileDestination(destination.href, event)
                    }
                    onAction={queueMobileAction}
                  />
                </nav>
              </DialogBody>
            </Dialog>
          ) : null}
        </div>

        <div className="flex min-w-0 flex-1 flex-col pb-2 lg:pt-2 lg:pr-2 lg:pl-64">
          <div className="min-w-0 grow bg-(--compass-surface) p-6 lg:rounded-md lg:p-10 lg:shadow-sm">
            <div className="mx-auto w-full min-w-0 max-w-6xl">
              <Outlet />
            </div>
          </div>
        </div>
      </div>
      <FeedbackPopup />
    </>
  );
};

export default Layout;
